import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  MinusIcon,
  PlusIcon,
  Share2,
  CheckCheck,
  Check,
  TableIcon,
  X,
  ChartCandlestick,
  Plus,
  MoreHorizontal,
  Loader2,
  ChevronDown,
  Play,
  Pause,
  Maximize2,
  Minimize2,
  Move,
  ZoomIn,
  RotateCcw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { InteractionMode } from '@/components/pages/AssetsStudioPage/MainContent/SensorData/Waveform/components';
import { FeatureOption, CustomLinesConfig } from '../../types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BaseTooltip } from '@/components';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { HarmonicsPopover } from './HarmonicsPopover';
import { SideBandsPopover } from './SideBandsPopover';
import { MarkerPopover } from './MarkerPopover';
import { DrawingToolsPopover } from '../drawing/DrawingToolsPopover';
import { generateTestId } from '@/utils/shared/generateTestIds';

interface DataLayerToolbarProps {
  sectionKey: string;
  isExpanded: boolean;
  onToggleSection?: (key: string) => void;

  title?: string;
  unit?: string;

  showSplitToggle?: boolean;
  isSplit?: boolean;
  onToggleSplitCharts?: (key: string) => void;
  // onDownload?: () => void;
  // hasChartData?: boolean; // Whether chart has data to export

  showMultiSelect?: boolean;
  multiSelect?: boolean;
  onToggleMultiSelect?: () => void;

  featureOptions?: FeatureOption[];
  onFeatureChange?: (featureKey: string, isActive: boolean, sectionKey: string) => Promise<void>;

  // Add loading states for features
  loadingFeatures?: Record<string, boolean>;
  isFeatureLoading?: boolean;

  verticalLines?: any[];
  showComparisonTable?: boolean;
  onToggleCompareModal?: (key: string, open: boolean) => void;
  onClearLines?: () => void;

  markingTools?: {
    markers?: boolean;
    harmonics?: boolean;
    sideBands?: boolean;
    measure?: boolean;
    drawing?: boolean;
  };
  intervalLines?: any[];
  onAddIntervalLines?: (lines: any[]) => void;
  onClearIntervalLines?: () => void;
  xAxisData?: any[];
  xAxisType?: 'date' | 'numeric' | 'category';

  // Marker state
  onEnterMarkerSelectionMode?: () => void;
  isMarkerSelectionMode?: boolean;
  markerLabel?: string; // Custom label for marker button (e.g., "Timestamps" for sensors, "Markers" for spectrum)

  // Custom vertical lines
  customVerticalLines?: any;
  customLinesConfig?: CustomLinesConfig; // Configuration for custom lines behavior
  onEnterCustomLineMode?: (groupId: string) => void;
  isCustomLineMode?: string | null; // groupId of active custom line mode
  dataLayer?: string; // Data layer identifier (e.g. 'sensors', 'spectrum_analysis')
  chartId?: string; // Chart identifier for filtering when sync=false

  // Harmonics state
  harmonicSelections?: any[];
  onAddHarmonicSelection?: (selection: any) => void;
  onUpdateHarmonicSelection?: (id: string, updates: any) => void;
  onRemoveHarmonicSelection?: (id: string) => void;
  onRemoveAllHarmonics?: () => void;
  onFocusHarmonic?: (id: string) => void;
  onResetHarmonic?: (id: string) => void;
  onEnterHarmonicSelectionMode?: () => void;
  onGenerateHarmonics?: (
    startingPoint: number,
    multiplier: number,
    label: string,
    intervalType?: 'seconds' | 'minutes' | 'hours' | 'days',
    xAxisType?: 'date' | 'numeric' | 'category',
    chartId?: string
  ) => void;
  isHarmonicSelectionMode?: boolean;

  // Side Bands state
  sideBandsSelections?: any[];
  onEnterSideBandsSelectionMode?: () => void;
  isSideBandsSelectionMode?: boolean;

  showCompareToggle?: boolean;

  // Pending harmonic/sideband creation state (waiting for second click)
  pendingHarmonicCreation?: {
    centerPoint: number;
    chartId: string;
    sectionKey: string;
    type: 'harmonic' | 'sideBands';
    labelPrefix: string;
    color: string;
    xAxisType: 'date' | 'numeric' | 'category';
    groupId: string;
  } | null;

  isExpandable?: boolean;

  // Waveform controls (for waveform data layer)
  isWaveformDataLayer?: boolean;
  waveformControls?: {
    play: () => void;
    pause: () => void;
    stop: () => void;
    zoomIn: () => void;
    zoomOut: () => void;
    resetZoom: () => void;
    seekTo: (timeInSeconds: number) => void;
    isReady: boolean;
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    zoomLevel: number;
    formatTime: (seconds: number) => string;
  } | null;
  waveformPlayMode?: 'acceleration' | 'velocity';
  onWaveformPlayModeChange?: (mode: 'acceleration' | 'velocity') => void;
  onDownloadFullWaveform?: () => void;
  isFullWaveformMode?: boolean;
  // Waveform interaction mode
  waveformInteractionMode?: InteractionMode;
  onToggleWaveformInteractionMode?: () => void;
  onResetWaveformZoom?: () => void;
  // Waveform rendering variant: 'wavesurfer' or 'plotly'
  waveformVariant?: 'wavesurfer' | 'plotly';
}

/**
 * Editable timestamp component for waveform fine-tuning
 * Click to edit, type time in M:SS.ms format, press Enter or blur to seek
 */
const EditableTimestamp: React.FC<{
  currentTime: number;
  duration: number;
  formatTime: (seconds: number) => string;
  onSeek?: (timeInSeconds: number) => void;
}> = ({ currentTime, duration, formatTime, onSeek }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [minutes, setMinutes] = useState('0');
  const [seconds, setSeconds] = useState('00');
  const [centiseconds, setCentiseconds] = useState('00');
  const minutesRef = useRef<HTMLInputElement>(null);
  const secondsRef = useRef<HTMLInputElement>(null);
  const centisecondsRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    if (!onSeek) return;
    // Parse current time into components
    const mins = Math.floor(currentTime / 60);
    const secs = Math.floor(currentTime % 60);
    const cs = Math.floor((currentTime % 1) * 100);
    setMinutes(mins.toString());
    setSeconds(secs.toString().padStart(2, '0'));
    setCentiseconds(cs.toString().padStart(2, '0'));
    setIsEditing(true);
  }, [currentTime, onSeek]);

  const handleSubmit = useCallback(() => {
    if (onSeek) {
      const mins = parseInt(minutes || '0', 10);
      const secs = parseInt(seconds || '0', 10);
      const cs = parseInt(centiseconds || '0', 10);
      const totalSeconds = mins * 60 + secs + cs / 100;
      // Clamp to valid range
      const clampedTime = Math.max(0, Math.min(totalSeconds, duration));
      onSeek(clampedTime);
    }
    setIsEditing(false);
  }, [minutes, seconds, centiseconds, duration, onSeek]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, field: 'minutes' | 'seconds' | 'centiseconds') => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    } else if (e.key === 'ArrowRight' || e.key === ':' || e.key === '.') {
      e.preventDefault();
      if (field === 'minutes') secondsRef.current?.focus();
      else if (field === 'seconds') centisecondsRef.current?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (field === 'centiseconds') secondsRef.current?.focus();
      else if (field === 'seconds') minutesRef.current?.focus();
    }
  }, [handleSubmit]);

  // Focus minutes input when editing starts
  useEffect(() => {
    if (isEditing && minutesRef.current) {
      minutesRef.current.focus();
      minutesRef.current.select();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <span className="text-sm text-[#55687d] font-mono tabular-nums flex items-center gap-0.5">
        <input
          ref={minutesRef}
          type="text"
          value={minutes}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '');
            if (val.length <= 2) setMinutes(val);
            if (val.length === 2) secondsRef.current?.focus();
          }}
          onKeyDown={(e) => handleKeyDown(e, 'minutes')}
          className="w-6 px-0.5 py-0 text-sm font-mono bg-white border border-primary rounded text-center focus:outline-none focus:ring-1 focus:ring-primary"
          inputMode="numeric"
          maxLength={2}
        />
        <span>:</span>
        <input
          ref={secondsRef}
          type="text"
          value={seconds}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '');
            if (val.length <= 2 && parseInt(val || '0') < 60) {
              setSeconds(val.padStart(2, '0'));
              if (val.length === 2) centisecondsRef.current?.focus();
            }
          }}
          onKeyDown={(e) => handleKeyDown(e, 'seconds')}
          onBlur={() => setSeconds(s => s.padStart(2, '0'))}
          className="w-8 px-0.5 py-0 text-sm font-mono bg-white border border-primary rounded text-center focus:outline-none focus:ring-1 focus:ring-primary"
          inputMode="numeric"
          maxLength={2}
        />
        <span>.</span>
        <input
          ref={centisecondsRef}
          type="text"
          value={centiseconds}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '');
            if (val.length <= 2) setCentiseconds(val.padStart(2, '0'));
          }}
          onKeyDown={(e) => handleKeyDown(e, 'centiseconds')}
          onBlur={handleSubmit}
          className="w-8 px-0.5 py-0 text-sm font-mono bg-white border border-primary rounded text-center focus:outline-none focus:ring-1 focus:ring-primary"
          inputMode="numeric"
          maxLength={2}
        />
        <span className="mx-1">/</span>
        <span>{formatTime(duration)}</span>
      </span>
    );
  }

  return (
    <span className="text-sm text-[#55687d] font-mono tabular-nums">
      <span
        onClick={handleClick}
        className={onSeek ? 'cursor-pointer hover:text-primary hover:underline' : ''}
        title={onSeek ? 'Click to edit time' : undefined}
      >
        {formatTime(currentTime)}
      </span>
      {' / '}
      {formatTime(duration)}
    </span>
  );
};

export const DataLayerToolbar = ({
  sectionKey,
  isExpanded,
  onToggleSection,

  title,
  unit: _unit,

  showSplitToggle = false,
  isSplit = false,
  onToggleSplitCharts,
  // onDownload,
  // hasChartData = false,

  showMultiSelect = false,
  multiSelect = false,
  onToggleMultiSelect,

  featureOptions = [],
  onFeatureChange,
  loadingFeatures = {},
  isFeatureLoading = false,

  verticalLines = [],
  showComparisonTable = false,
  onToggleCompareModal,
  onClearLines,

  markingTools,
  intervalLines = [],
  onAddIntervalLines,
  onClearIntervalLines,
  xAxisData = [],
  xAxisType = 'date',

  onEnterMarkerSelectionMode,
  isMarkerSelectionMode = false,
  markerLabel = 'Markers',

  customVerticalLines,
  customLinesConfig,
  onEnterCustomLineMode,
  isCustomLineMode = null,
  dataLayer,
  chartId,

  harmonicSelections = [],
  onAddHarmonicSelection,
  onUpdateHarmonicSelection,
  onRemoveHarmonicSelection,
  onRemoveAllHarmonics,
  onFocusHarmonic,
  onResetHarmonic,
  onEnterHarmonicSelectionMode,
  onGenerateHarmonics,
  isHarmonicSelectionMode = false,

  sideBandsSelections = [],
  onEnterSideBandsSelectionMode,
  isSideBandsSelectionMode = false,

  showCompareToggle = false,

  pendingHarmonicCreation = null,
  isExpandable = true,

  // Waveform controls
  isWaveformDataLayer = false,
  waveformControls = null,
  waveformPlayMode = 'acceleration',
  onWaveformPlayModeChange,
  onDownloadFullWaveform,
  isFullWaveformMode = false,
  waveformInteractionMode,
  onToggleWaveformInteractionMode,
  onResetWaveformZoom,
  waveformVariant = 'plotly',
}: DataLayerToolbarProps) => {
  // Check if buttons should be disabled due to pending harmonic/sideband creation
  const isPendingCreation = !!pendingHarmonicCreation;

  // Extract custom lines configuration with defaults
  const syncCustomLines = customLinesConfig?.sync ?? false;
  const enableDynamicCustomLineButtons = customLinesConfig?.enableDynamicButtons ?? false;

  const basicFeaturesLimit = 2;
  const basicFeatureOptions = featureOptions.slice(0, basicFeaturesLimit);
  const extraFeatureOptions = featureOptions.slice(basicFeaturesLimit);
  const activeExtraFeatureOptions = extraFeatureOptions.filter((f) => f.isActive);
  const hasActiveExtraFeatures = activeExtraFeatureOptions.length > 0;
  const isDot = false;

  const extraFeaturesLabel = hasActiveExtraFeatures
    ? `${activeExtraFeatureOptions[0].label} ${
        activeExtraFeatureOptions.length > 1 ? `+${activeExtraFeatureOptions.length - 1}` : ''
      }`
    : 'More Trends';

  // Helper function to check if a specific filter is loading
  const isFeatureLoadingState = (featureKey: string) => {
    return loadingFeatures[featureKey] || false;
  };

  // Helper function to handle feature change with loading state
  const handleFeatureChangeWithLoading = async (featureKey: string, isActive: boolean) => {
    if (isFeatureLoadingState(featureKey) || isFeatureLoading) {
      return; // Prevent multiple clicks while loading
    }

    await onFeatureChange?.(featureKey, isActive, sectionKey);
  };

  return (
    <div className="flex items-center gap-2">
      {/* Left: Title - fixed 280px when expanded to match legend, flexible when collapsed */}
      <div
        className={`flex items-center gap-2 ${
          isExpanded && isExpandable ? 'w-[280px] min-w-[280px] max-w-[280px]' : ''
        }`}
      >
        {isExpandable ? (
          onToggleSection && (
            <Button
              variant="ghost"
              size="sm"
              className={`py-2 justify-start ${isExpanded ? 'w-full' : ''}`}
              onClick={() => onToggleSection(sectionKey)}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4 flex-shrink-0" />
              ) : (
                <PlusIcon className="w-4 h-4 flex-shrink-0" />
              )}
              <SectionTitle title={title} isExpanded={isExpanded} />
            </Button>
          )
        ) : (
          <SectionTitle title={title} isExpanded={isExpanded} />
        )}
      </div>

      {/* Right: Toolbar Actions and Features - flexible width */}
      {isExpanded && (
        <div className="flex-1 flex items-center gap-2 flex-wrap justify-between">
          {/* Left: Marking Tools */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Marker Tool */}
            {markingTools?.markers && onEnterMarkerSelectionMode && (
              <MarkerPopover
                onEnterSelectionMode={onEnterMarkerSelectionMode}
                isSelectionMode={isMarkerSelectionMode}
                disabled={isFeatureLoading || isPendingCreation}
                label={markerLabel}
              />
            )}

            {/* Custom Vertical Line Buttons */}
            {(() => {
              // Don't show buttons if disabled
              if (!enableDynamicCustomLineButtons) return null;

              if (!customVerticalLines || !customVerticalLines.groups) return null;

              const filteredGroups = customVerticalLines.groups.filter((group: any) => {
                if (!group.visible) return false;

                // Filter groups based on sync setting - same logic as PlotlyChart
                if (!group.groupItems || !Array.isArray(group.groupItems)) return false;

                // Check if this group has any items that should show buttons in this section
                const shouldShow = group.groupItems.some((item: any) => {
                  if (syncCustomLines) {
                    // When synced, show buttons if any item belongs to the same data layer
                    return !item.dataLayer || item.dataLayer === dataLayer;
                  } else {
                    // When not synced, show buttons if any item belongs to this specific chart
                    return !item.chartId || item.chartId === chartId;
                  }
                });

                return shouldShow;
              });

              return filteredGroups.map((group: any) => (
                <BaseTooltip key={group.groupId} content={`Click to add ${group.groupLabel} by clicking on the chart`}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`gap-2 ${isCustomLineMode === group.groupId ? 'bg-secondary text-primary-500 font-semibold' : 'text-[#55687d]'}`}
                    onClick={() => onEnterCustomLineMode?.(group.groupId)}
                    disabled={isFeatureLoading || isPendingCreation}
                  >
                    <Plus className="w-4 h-4" />
                    <span>{group.groupIcon || '📍'}</span>
                    {group.groupLabel}
                  </Button>
                </BaseTooltip>
              ));
            })()}

            {/* Harmonics Tool */}
            {markingTools?.harmonics && onAddHarmonicSelection && (
              <HarmonicsPopover
                selections={harmonicSelections}
                onAddSelection={onAddHarmonicSelection}
                onUpdateSelection={onUpdateHarmonicSelection!}
                onRemoveSelection={onRemoveHarmonicSelection!}
                onRemoveAll={onRemoveAllHarmonics!}
                onFocus={onFocusHarmonic!}
                onReset={onResetHarmonic!}
                onEnterSelectionMode={onEnterHarmonicSelectionMode!}
                onGenerateHarmonics={onGenerateHarmonics!}
                isSelectionMode={isHarmonicSelectionMode}
                disabled={isFeatureLoading || isPendingCreation}
                xAxisType={xAxisType}
              />
            )}

            {/* Side Bands Tool */}
            {markingTools?.sideBands && onEnterSideBandsSelectionMode && (
              <SideBandsPopover
                selections={sideBandsSelections}
                onEnterSelectionMode={onEnterSideBandsSelectionMode}
                isSelectionMode={isSideBandsSelectionMode}
                disabled={isFeatureLoading || isPendingCreation}
                xAxisType={xAxisType}
              />
            )}

            {/* Drawing Tools */}
            {/* Hidden per user request */}
            {/* {markingTools?.drawing && (
              <DrawingToolsPopover disabled={isFeatureLoading || isPendingCreation} />
            )} */}

            {/* Compare Table Toggle (Values) */}
            {onToggleCompareModal && showCompareToggle && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 text-[#55687d]"
                disabled={
                  verticalLines?.length === 0 || isFeatureLoading || isHarmonicSelectionMode || isPendingCreation
                }
                onClick={() => onToggleCompareModal(sectionKey, !showComparisonTable)}
              >
                <TableIcon />
                {showComparisonTable ? 'Hide Values' : 'Values'}
              </Button>
            )}

            {/* Clear All Button */}
            {verticalLines?.length > 0 && onClearLines && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 text-[#55687d]"
                disabled={isFeatureLoading || isHarmonicSelectionMode || isPendingCreation}
                onClick={() => {
                  onClearLines();
                }}
              >
                <X />
                Clear
              </Button>
            )}
          </div>

          {/* Right: Waveform Controls + Features + Split Charts */}
          <div className="flex items-center gap-4 flex-wrap">
            {/* Waveform Controls */}
            {isWaveformDataLayer && (
              <TooltipProvider>
                <div className="flex items-center gap-2">
                  {/* Interaction Mode Toggle - only for WaveSurfer variant */}
                  {waveformVariant === 'wavesurfer' && onToggleWaveformInteractionMode && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={onToggleWaveformInteractionMode}
                          className={cn(
                            'h-8 px-2 gap-1.5 text-xs font-medium transition-colors',
                            waveformInteractionMode === 'zoom'
                              ? 'text-violet-600 bg-violet-100 hover:bg-violet-200 dark:text-violet-400 dark:bg-violet-900/30'
                              : 'text-[#55687d] hover:text-foreground'
                          )}
                        >
                          {waveformInteractionMode === 'zoom' ? (
                            <ZoomIn className="h-3.5 w-3.5" />
                          ) : (
                            <Move className="h-3.5 w-3.5" />
                          )}
                          <span>{waveformInteractionMode === 'zoom' ? 'Zoom Mode' : 'Selection Mode'}</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {waveformInteractionMode === 'zoom'
                          ? 'Drag to zoom into a region'
                          : 'Drag to select playback region'}
                      </TooltipContent>
                    </Tooltip>
                  )}

                  {/* Reset Zoom Button - only for WaveSurfer variant */}
                  {waveformVariant === 'wavesurfer' && onResetWaveformZoom && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={onResetWaveformZoom}
                          className="h-8 px-2 gap-1.5 text-xs text-[#55687d] hover:text-foreground"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          <span>Reset Zoom</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Reset to fit entire waveform</TooltipContent>
                    </Tooltip>
                  )}

                {/* Separator - only show if WaveSurfer controls are visible */}
                {waveformVariant === 'wavesurfer' && <div className="w-px h-5 bg-border mx-1" />}

                {/* Play Acceleration */}
                <Button
                  variant="ghost"
                  size="sm"
                  className={`gap-2 h-8 text-[#55687d] ${waveformPlayMode === 'acceleration' && waveformControls?.isPlaying ? 'bg-muted' : ''}`}
                  onClick={() => {
                    onWaveformPlayModeChange?.('acceleration');
                    if (waveformControls?.isReady) {
                      if (waveformPlayMode !== 'acceleration' || !waveformControls.isPlaying) {
                        waveformControls.play();
                      } else {
                        waveformControls.pause();
                      }
                    }
                  }}
                  disabled={!waveformControls?.isReady}
                >
                  {waveformPlayMode === 'acceleration' && waveformControls?.isPlaying ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Acceleration
                </Button>

                {/* Play Velocity */}
                <Button
                  variant="ghost"
                  size="sm"
                  className={`gap-2 h-8 text-[#55687d] ${waveformPlayMode === 'velocity' && waveformControls?.isPlaying ? 'bg-muted' : ''}`}
                  onClick={() => {
                    onWaveformPlayModeChange?.('velocity');
                    if (waveformControls?.isReady) {
                      if (waveformPlayMode !== 'velocity' || !waveformControls.isPlaying) {
                        waveformControls.play();
                      } else {
                        waveformControls.pause();
                      }
                    }
                  }}
                  disabled={!waveformControls?.isReady}
                >
                  {waveformPlayMode === 'velocity' && waveformControls?.isPlaying ? (
                    <Pause className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Velocity
                </Button>

                {/* Restart Button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-[#55687d]"
                      onClick={() => {
                        if (waveformControls?.isReady) {
                          waveformControls.stop();
                        }
                      }}
                      disabled={!waveformControls?.isReady}
                    >
                      <RotateCcw className="w-3 h-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Restart playback from beginning</TooltipContent>
                </Tooltip>

                {/* Editable Time Display */}
                <EditableTimestamp
                  currentTime={waveformControls?.currentTime || 0}
                  duration={waveformControls?.duration || 0}
                  formatTime={waveformControls?.formatTime || ((s) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}.${Math.floor((s % 1) * 100).toString().padStart(2, '0')}`)}
                  onSeek={waveformControls?.seekTo}
                />

                {/* Full Waveform Button - toggles between 5 sec and full waveform */}
                <Button
                  variant="ghost"
                  size="sm"
                  className={`gap-2 h-8 ${isFullWaveformMode ? 'text-primary-600 bg-primary-50' : 'text-[#55687d]'}`}
                  onClick={onDownloadFullWaveform}
                  title={isFullWaveformMode ? "Switch to 5 second waveform" : "Load full waveform"}
                >
                  {isFullWaveformMode ? (
                    <>
                      <Minimize2 className="w-4 h-4" />
                      5 Sec Waveform
                    </>
                  ) : (
                    <>
                      <Maximize2 className="w-4 h-4" />
                      Full Waveform
                    </>
                  )}
                </Button>

                </div>
              </TooltipProvider>
            )}

            {/* Optional Badge Filters (Features) */}
            {featureOptions?.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {basicFeatureOptions.map(({ key, label, isActive }) => {
                  const activeCount = featureOptions.filter((f) => f.isActive).length;
                  const isLastActive = isActive && activeCount === 1;
                  const isDisabled = (multiSelect && isLastActive) || isFeatureLoading;
                  const isThisFeatureLoading = isFeatureLoadingState(key);

                  return (
                    <Badge
                      key={key}
                      className={`px-3 py-1 text-xs bg-secondary flex items-center gap-1 ${
                        isActive ? 'bg-primary-500 text-white' : 'cursor-pointer'
                      } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => {
                        if (isDisabled) return;
                        if (!isLastActive) handleFeatureChangeWithLoading(key, !isActive);
                      }}
                      data-testid={`trend_${generateTestId(label)}`}
                    >
                      {isThisFeatureLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                      {label}
                    </Badge>
                  );
                })}

                {featureOptions.length > 2 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <div className="relative">
                        <button
                          className={`cursor-pointer px-3 py-1.5 pb-1 text-xs flex items-center bg-secondary rounded-full font-semibold gap-1.5 ${
                            !isDot && hasActiveExtraFeatures ? '!bg-primary-500 text-white' : ''
                          } ${isFeatureLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={isFeatureLoading}
                        >
                          {hasActiveExtraFeatures
                            ? `${activeExtraFeatureOptions[0].label} ${
                                activeExtraFeatureOptions.length > 1 ? `+${activeExtraFeatureOptions.length - 1}` : ''
                              }`
                            : 'More Trends'}
                          <MoreHorizontal size={14} />
                        </button>
                        {hasActiveExtraFeatures && isDot && (
                          <span className="absolute top-1 right-1 w-2 h-2 bg-primary-600 rounded-full border border-white" />
                        )}
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-2 space-y-2">
                      {extraFeatureOptions.map(({ key, label, isActive }) => {
                        const isThisFeatureLoading = isFeatureLoadingState(key);

                        return (
                          <div
                            key={key}
                            className={`flex items-center space-x-2 ${
                              isFeatureLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                            }`}
                            onClick={() => {
                              if (isFeatureLoading) return;
                              if (!multiSelect) {
                                handleFeatureChangeWithLoading(key, !isActive);
                              }
                            }}
                          >
                            {multiSelect ? (
                              <Checkbox
                                id={`filter-${key}`}
                                checked={isActive}
                                disabled={
                                  isFeatureLoading ||
                                  (multiSelect && isActive && featureOptions.filter((f) => f.isActive).length === 1)
                                }
                                onCheckedChange={(checked: boolean) => {
                                  if (isFeatureLoading) return;
                                  const activeCount = featureOptions.filter((f) => f.isActive).length;
                                  const isTryingToDeselectLast = isActive && activeCount === 1;
                                  if (multiSelect && isTryingToDeselectLast) return;
                                  handleFeatureChangeWithLoading(key, !!checked);
                                }}
                              />
                            ) : (
                              <Check className={`w-4 h-4 text-primary-500 ${isActive ? 'opacity-100' : 'opacity-0'}`} />
                            )}
                            <div className="flex items-center gap-2">
                              {isThisFeatureLoading && <Loader2 className="w-3 h-3 animate-spin text-primary-500" />}
                              <label
                                htmlFor={`filter-${key}`}
                                className={`text-sm ${isFeatureLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                {label}
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            )}

            {/* Optional Download Button - HIDDEN */}
            {/* {onDownload && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 text-[#55687d]"
                disabled={isFeatureLoading || (!hasChartData && verticalLines.length === 0)}
                onClick={onDownload}
                title={
                  !hasChartData && verticalLines.length === 0
                    ? 'No data available for export'
                    : verticalLines.length > 0
                    ? 'Download comparison data as Excel'
                    : 'Download chart data as Excel'
                }
              >
                <Download className="w-4 h-4" />
                Download
              </Button>
            )} */}

            {/* Optional Split Charts Toggle - moved to the end */}
            {showSplitToggle && onToggleSplitCharts && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-2 text-[#55687d]"
                      disabled={isFeatureLoading}
                      onClick={() => onToggleSplitCharts(sectionKey)}
                    >
                      <Share2 className="w-4 h-4" />
                      {isSplit ? 'Merge' : 'Split'}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isSplit
                      ? 'Merge charts into a single combined view'
                      : 'Split charts into separate individual views'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SectionTitle = ({ title, isExpanded }: { title?: string; isExpanded: boolean }) => {
  if (!title) return null;

  return (
    <div className="flex items-center pl-2 min-w-0 flex-1">
      <h2 className={`text-lg font-semibold text-[#01152b] ${isExpanded ? 'truncate' : ''}`}>{title}</h2>
    </div>
  );
};
